import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  cpSummary,
  cpListContactLeads,
  cpListRegistrations,
  cpListStrategyCalls,
  cpListConsultations,
  cpListChatLeads,
  cpGetIntegrations,
  cpUpdateIntegrations,
  cpTestCRMWebhook,
  cpWipeAllLeads,
  cpExportUrl,
  cpAnalyticsSummary,
  cpAnalyticsInsights,
  cpRegenerateInsights,
  cpListConsultants,
  cpCreateConsultant,
  cpUpdateConsultant,
  cpDeleteConsultant,
  cpUploadConsultantsCsv,
} from "@/lib/api";
import { API } from "@/lib/api";
import { TID } from "@/constants/testIds";
import {
  LogOut, Flame, ThermometerSun, Snowflake, Users, MailCheck, Video, Gift,
  Download, Search, Settings as SettingsIcon, Bot, MessageSquareText, Phone, Calendar,
  LayoutDashboard, RefreshCw, Send, CheckCircle2, XCircle, Eye, Trash2, AlertTriangle,
  BarChart3, TrendingUp, Globe2, Sparkles as SparklesIcon, MousePointerClick,
  Briefcase, Plus, Upload, Pencil,
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, BarChart, Bar, Cell, PieChart, Pie, Legend } from "recharts";
import RegistrationDetail from "@/components/RegistrationDetail";
import { toast } from "sonner";

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard, testId: TID.cpTabOverview },
  { id: "analytics", label: "Analytics", icon: BarChart3, testId: TID.cpTabAnalytics },
  { id: "contact", label: "Contact Leads", icon: MailCheck, testId: TID.cpTabContact },
  { id: "event", label: "Event Registrations", icon: Users, testId: TID.cpTabEvent },
  { id: "chat", label: "Chatbot Leads", icon: Bot, testId: TID.cpTabChat },
  { id: "consult", label: "Consultations", icon: Video, testId: TID.cpTabConsultations },
  { id: "strategy", label: "Strategy Calls", icon: Phone, testId: TID.cpTabStrategy },
  { id: "consultants", label: "Consultants", icon: Briefcase, testId: TID.cpTabConsultants },
  { id: "settings", label: "Settings", icon: SettingsIcon, testId: TID.cpTabSettings },
];

export default function CPPortal() {
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const [token, setToken] = useState(null);
  const [data, setData] = useState({
    summary: null, leads: [], regs: [], calls: [], consults: [], chats: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = localStorage.getItem("phc-admin-token");
    if (!t) {
      nav("/admin/login");
      return;
    }
    setToken(t);
    Promise.all([
      cpSummary(t),
      cpListContactLeads(t).catch(() => []),
      cpListRegistrations(t).catch(() => []),
      cpListStrategyCalls(t).catch(() => []),
      cpListConsultations(t).catch(() => []),
      cpListChatLeads(t).catch(() => []),
    ])
      .then(([s, c, r, sc, co, ch]) => {
        setData({ summary: s, leads: c, regs: r, calls: sc, consults: co, chats: ch });
        setLoading(false);
      })
      .catch(() => {
        localStorage.removeItem("phc-admin-token");
        nav("/admin/login");
      });
  }, [nav]);

  const logout = () => {
    localStorage.removeItem("phc-admin-token");
    nav("/admin/login");
  };

  if (loading || !data.summary) {
    return (
      <div className="min-h-screen bg-offwhite grid place-items-center">
        <div className="text-forest/60 text-sm">Loading portal&hellip;</div>
      </div>
    );
  }

  return (
    <div data-testid={TID.cpDashboard} className="min-h-screen bg-offwhite">
      {/* Header */}
      <header className="bg-forest text-cream border-b border-lime/30 sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-4 flex items-center justify-between">
          <div>
            <div className="eyebrow text-lime">Admin Console · Restricted</div>
            <div className="font-display text-xl">Pali Hill City · Chapter III</div>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-cream/70 hover:text-lime text-sm">
              Public site &nearr;
            </Link>
            <button data-testid={TID.cpLogout} onClick={logout} className="btn-outline-cream text-sm py-2">
              <LogOut size={14} /> Log out
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="flex items-center gap-1 md:gap-2 overflow-x-auto -mb-px scrollbar-none">
            {TABS.map((t) => (
              <button
                key={t.id}
                data-testid={t.testId}
                onClick={() => setTab(t.id)}
                className={`inline-flex items-center gap-2 px-3 md:px-4 py-2.5 text-sm border-b-2 whitespace-nowrap transition-colors ${
                  tab === t.id
                    ? "border-lime text-lime"
                    : "border-transparent text-cream/60 hover:text-cream"
                }`}
              >
                <t.icon size={14} />
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-5 md:px-10 py-8 md:py-10 space-y-8">
        {tab === "overview" && <Overview summary={data.summary} />}
        {tab === "analytics" && <AnalyticsTab token={token} />}
        {tab === "contact" && (
          <ContactTab leads={data.leads} />
        )}
        {tab === "event" && (
          <EventTab regs={data.regs} />
        )}
        {tab === "chat" && (
          <ChatTab chats={data.chats} />
        )}
        {tab === "consult" && (
          <ConsultTab consults={data.consults} />
        )}
        {tab === "strategy" && (
          <StrategyTab calls={data.calls} />
        )}
        {tab === "consultants" && <ConsultantsTab token={token} />}
        {tab === "settings" && <SettingsTab token={token} />}
      </main>
    </div>
  );
}

/* ------------------------------------------------------------------
 * Overview (existing dashboard)
 * ------------------------------------------------------------------ */
function Overview({ summary }) {
  return (
    <>
      {/* Phase status */}
      <div className="grid md:grid-cols-3 gap-4">
        <StatCard title="Phase I" main={summary.phase_1.status} sub={`${summary.phase_1.plots} plots · ${summary.phase_1.sqft.toLocaleString()} sqft`} />
        <StatCard title="Phase II" main={summary.phase_2.status} sub={`${summary.phase_2.plots} plots · ${summary.phase_2.sqft.toLocaleString()} sqft`} />
        <StatCard title="Chapter III" main="Live · Registration open" sub="Launch Sept 12–13, 2026" highlight />
      </div>

      {/* Leads summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
        <SmallStat label="Contact leads" value={summary.leads.contact_leads} icon={MailCheck} />
        <SmallStat label="Event · Full" value={summary.leads.event_registrations} icon={Users} tone="warm" />
        <SmallStat label="Event · Step 1 only" value={summary.leads.event_registrations_partial || 0} icon={Snowflake} tone="cold" />
        <SmallStat label="Chatbot leads" value={summary.leads.chat_leads || 0} icon={Bot} />
        <SmallStat label="Consultations" value={summary.leads.consultations || 0} icon={Video} />
        <SmallStat label="Hot" value={summary.leads.hot} icon={Flame} tone="hot" />
        <SmallStat label="Warm" value={summary.leads.warm} icon={ThermometerSun} tone="warm" />
      </div>

      {/* Appreciation + Competitor */}
      <div className="grid md:grid-cols-12 gap-6">
        <div className="md:col-span-7 bg-white border border-forest/10 rounded-lg p-4 md:p-6 h-[340px]">
          <div className="eyebrow text-teal mb-2">Layout Appreciation Index</div>
          <div className="font-display text-forest text-xl">Indexed to 100 at 2019</div>
          <div className="h-[240px] mt-2">
            <ResponsiveContainer>
              <LineChart data={summary.appreciation} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="#0B4A3D" strokeOpacity={0.08} vertical={false} />
                <XAxis dataKey="year" stroke="#0B4A3D" strokeOpacity={0.5} tick={{ fill: "#0B4A3D", fontSize: 12 }} />
                <YAxis stroke="#0B4A3D" strokeOpacity={0.5} tick={{ fill: "#0B4A3D", fontSize: 12 }} />
                <Tooltip contentStyle={{ background: "#0B4A3D", color: "#F4F4EF", border: "none" }} />
                <Line dataKey="value" stroke="#0B4A3D" strokeWidth={2.5} dot={{ fill: "#A6CE39", r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="md:col-span-5 bg-white border border-forest/10 rounded-lg p-4 md:p-6 space-y-3">
          <div className="eyebrow text-teal">Competitor Context · Admin-only</div>
          <div className="font-display text-forest text-xl">Per-sqft benchmarks</div>
          <table className="w-full text-sm mt-2">
            <thead>
              <tr className="text-forest/50 text-xs border-b border-forest/15">
                <th className="text-left py-2 font-normal">Layout</th>
                <th className="text-right py-2 font-normal">&#8377;/sqft</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-forest/10">
              {summary.competitor_context.map((c) => (
                <tr key={c.brand}>
                  <td className="py-3">
                    <div className="text-forest font-semibold">{c.brand}</div>
                    <div className="text-forest/50 text-xs">{c.note}</div>
                  </td>
                  <td className="py-3 text-right font-display text-forest text-xl">
                    &#8377;{c.per_sqft.toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------
 * Reusable tab layout wrapper
 * ------------------------------------------------------------------ */
function TabPanel({ title, subtitle, count, children, exportKind, exportTestId }) {
  const href = exportKind ? cpExportUrl(exportKind) : null;
  const [downloading, setDownloading] = useState(false);
  const token = localStorage.getItem("phc-admin-token");

  const download = async (e) => {
    e.preventDefault();
    if (!href || downloading) return;
    setDownloading(true);
    try {
      const resp = await fetch(href, { headers: { "X-CP-Token": token } });
      if (!resp.ok) throw new Error("Export failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${exportKind}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Excel file downloaded.");
    } catch {
      toast.error("Couldn't download the export. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="bg-white border border-forest/10 rounded-lg overflow-hidden">
      <div className="p-4 md:p-6 border-b border-forest/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="eyebrow text-teal">{subtitle}</div>
          <div className="font-display text-forest text-xl">
            {title}
            {typeof count === "number" && (
              <span className="ml-2 text-sm text-forest/50 font-body align-middle">
                &middot; {count} total
              </span>
            )}
          </div>
        </div>
        {exportKind && (
          <button
            data-testid={exportTestId}
            onClick={download}
            disabled={downloading}
            className="btn-primary text-sm self-start sm:self-auto"
          >
            <Download size={14} />
            {downloading ? "Preparing…" : "Download XLS"}
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

function SearchBar({ value, onChange, placeholder }) {
  return (
    <div className="p-4 md:p-5 border-b border-forest/10">
      <div className="relative max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-forest/40" />
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder || "Search by name, email, or mobile…"}
          className="w-full pl-9 pr-3 py-2 text-sm bg-cream/60 border border-forest/15 rounded-md focus:outline-none focus:border-forest text-forest placeholder:text-forest/40"
        />
      </div>
    </div>
  );
}

function EmptyRow({ cols, message }) {
  return (
    <tr>
      <td colSpan={cols} className="py-10 text-center text-forest/40 text-sm">
        {message}
      </td>
    </tr>
  );
}

/* ------------------------------------------------------------------
 * Contact Leads tab
 * ------------------------------------------------------------------ */
function ContactTab({ leads }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(
    () => leads.filter((l) => matches(l, q, ["name", "email", "mobile", "city"])),
    [leads, q]
  );
  return (
    <TabPanel
      title="Contact leads"
      subtitle="Sitewide inbound submissions"
      count={leads.length}
      exportKind="contact-leads"
      exportTestId={TID.cpExportContact}
    >
      <SearchBar value={q} onChange={setQ} />
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-forest/50 text-xs border-b border-forest/15">
              <th className="text-left py-3 px-4 font-normal">Created</th>
              <th className="text-left py-3 px-4 font-normal">Name</th>
              <th className="text-left py-3 px-4 font-normal">Mobile</th>
              <th className="text-left py-3 px-4 font-normal">Email</th>
              <th className="text-left py-3 px-4 font-normal">City</th>
              <th className="text-left py-3 px-4 font-normal">Source</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-forest/10">
            {filtered.length === 0 && <EmptyRow cols={6} message="No contact leads yet." />}
            {filtered.map((l) => (
              <tr key={l.id} className="text-forest">
                <td className="py-3 px-4 text-forest/60 text-xs">{fmtDate(l.created_at)}</td>
                <td className="py-3 px-4 font-medium">{l.name}</td>
                <td className="py-3 px-4 text-forest/70">{l.mobile}</td>
                <td className="py-3 px-4 text-forest/70 text-xs">{l.email}</td>
                <td className="py-3 px-4 text-forest/70">{l.city}</td>
                <td className="py-3 px-4 text-forest/50 text-xs">{l.source}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </TabPanel>
  );
}

/* ------------------------------------------------------------------
 * Event Registrations tab
 * ------------------------------------------------------------------ */
function EventTab({ regs }) {
  const [q, setQ] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const filtered = useMemo(() => {
    return regs
      .filter((r) => (stageFilter === "all" ? true : (r.stage || "completed") === stageFilter))
      .filter((r) => matches(r.payload || {}, q, ["name", "email", "mobile", "city"]));
  }, [regs, q, stageFilter]);

  const completed = regs.filter((r) => (r.stage || "completed") === "completed").length;
  const partial = regs.filter((r) => r.stage === "step_1_only").length;

  return (
    <TabPanel
      title="Event registrations"
      subtitle="Chapter III · Lead scoring feed"
      count={regs.length}
      exportKind="event-registrations"
      exportTestId={TID.cpExportEvent}
    >
      <div className="px-4 md:px-5 pt-4 flex items-center gap-3 flex-wrap">
        <StageChip
          label={`All (${regs.length})`}
          active={stageFilter === "all"}
          onClick={() => setStageFilter("all")}
        />
        <StageChip
          label={`Completed (${completed})`}
          active={stageFilter === "completed"}
          onClick={() => setStageFilter("completed")}
          tone="ok"
        />
        <StageChip
          label={`Step 1 only · abandoned (${partial})`}
          active={stageFilter === "step_1_only"}
          onClick={() => setStageFilter("step_1_only")}
          tone="warn"
        />
      </div>
      <SearchBar value={q} onChange={setQ} />
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-forest/50 text-xs border-b border-forest/15">
              <th className="text-left py-3 px-4 font-normal">Created</th>
              <th className="text-left py-3 px-4 font-normal">Name</th>
              <th className="text-left py-3 px-4 font-normal">Mobile</th>
              <th className="text-left py-3 px-4 font-normal">Occupation</th>
              <th className="text-left py-3 px-4 font-normal">Ticket</th>
              <th className="text-left py-3 px-4 font-normal">Prize</th>
              <th className="text-right py-3 px-4 font-normal">Score</th>
              <th className="text-right py-3 px-4 font-normal">Stage / Band</th>
              <th className="text-right py-3 px-4 font-normal w-24">Details</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-forest/10">
            {filtered.length === 0 && <EmptyRow cols={9} message="No registrations match this filter." />}
            {filtered.map((r) => {
              const p = r.payload || {};
              const partialRow = (r.stage || "completed") === "step_1_only";
              return (
                <tr
                  key={r.id}
                  className="text-forest hover:bg-cream/40 cursor-pointer transition-colors"
                  onClick={() => setSelected(r)}
                >
                  <td className="py-3 px-4 text-forest/60 text-xs">{fmtDate(r.created_at)}</td>
                  <td className="py-3 px-4 font-medium">{p.name}</td>
                  <td className="py-3 px-4 text-forest/70">{p.mobile}</td>
                  <td className="py-3 px-4 text-forest/70 text-xs">{p.occupation || "—"}</td>
                  <td className="py-3 px-4 font-mono text-xs text-forest/70">{r.ticket?.ticket_no || "—"}</td>
                  <td className="py-3 px-4 text-forest/70 text-xs">
                    {r.prize?.label || <span className="text-forest/30">—</span>}
                  </td>
                  <td className="py-3 px-4 text-right font-display text-lg">
                    {r.lead_score?.total ?? "—"}
                  </td>
                  <td className="py-3 px-4 text-right">
                    {partialRow ? (
                      <StageChip label="Abandoned" tone="warn" small />
                    ) : (
                      <BandChip band={r.lead_score?.band} />
                    )}
                  </td>
                  <td className="py-3 px-4 text-right">
                    <button
                      data-testid={`${TID.regDetailViewBtn}-${r.id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelected(r);
                      }}
                      className="inline-flex items-center gap-1.5 text-teal hover:text-forest text-xs font-semibold px-2.5 py-1.5 rounded-md hover:bg-cream transition-colors"
                    >
                      <Eye size={13} />
                      View
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <RegistrationDetail registration={selected} onClose={() => setSelected(null)} />
    </TabPanel>
  );
}

/* ------------------------------------------------------------------
 * Chatbot Leads tab
 * ------------------------------------------------------------------ */
function ChatTab({ chats }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(
    () => chats.filter((c) => matches(c, q, ["name", "email", "mobile"])),
    [chats, q]
  );
  return (
    <TabPanel
      title="Chatbot lead submissions"
      subtitle="Peat · Lead-gated conversations"
      count={chats.length}
      exportKind="chat-leads"
      exportTestId={TID.cpExportChat}
    >
      <SearchBar value={q} onChange={setQ} />
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-forest/50 text-xs border-b border-forest/15">
              <th className="text-left py-3 px-4 font-normal">Created</th>
              <th className="text-left py-3 px-4 font-normal">Name</th>
              <th className="text-left py-3 px-4 font-normal">Mobile</th>
              <th className="text-left py-3 px-4 font-normal">Email</th>
              <th className="text-left py-3 px-4 font-normal">Session</th>
              <th className="text-left py-3 px-4 font-normal">Source</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-forest/10">
            {filtered.length === 0 && <EmptyRow cols={6} message="No chatbot leads yet." />}
            {filtered.map((c) => (
              <tr key={c.id} className="text-forest">
                <td className="py-3 px-4 text-forest/60 text-xs">{fmtDate(c.created_at)}</td>
                <td className="py-3 px-4 font-medium">{c.name}</td>
                <td className="py-3 px-4 text-forest/70">{c.mobile}</td>
                <td className="py-3 px-4 text-forest/70 text-xs">{c.email}</td>
                <td className="py-3 px-4 text-forest/50 text-[11px] font-mono">
                  {c.session_id?.slice(0, 8)}&hellip;
                </td>
                <td className="py-3 px-4 text-forest/50 text-xs">{c.source}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </TabPanel>
  );
}

/* ------------------------------------------------------------------
 * Consultations tab
 * ------------------------------------------------------------------ */
function ConsultTab({ consults }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(
    () => consults.filter((c) => matches(c, q, ["name", "email", "mobile", "topic"])),
    [consults, q]
  );
  return (
    <TabPanel
      title="Consultation bookings"
      subtitle="Google Calendar · Online consultations"
      count={consults.length}
      exportKind="consultations"
      exportTestId={TID.cpExportConsult}
    >
      <SearchBar value={q} onChange={setQ} />
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-forest/50 text-xs border-b border-forest/15">
              <th className="text-left py-3 px-4 font-normal">Created</th>
              <th className="text-left py-3 px-4 font-normal">Name</th>
              <th className="text-left py-3 px-4 font-normal">Mobile</th>
              <th className="text-left py-3 px-4 font-normal">When (IST)</th>
              <th className="text-left py-3 px-4 font-normal">Topic</th>
              <th className="text-left py-3 px-4 font-normal">Source</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-forest/10">
            {filtered.length === 0 && <EmptyRow cols={6} message="No consultations booked yet." />}
            {filtered.map((c) => (
              <tr key={c.id} className="text-forest">
                <td className="py-3 px-4 text-forest/60 text-xs">{fmtDate(c.created_at)}</td>
                <td className="py-3 px-4 font-medium">{c.name}</td>
                <td className="py-3 px-4 text-forest/70">{c.mobile}</td>
                <td className="py-3 px-4 text-forest/70 text-xs">
                  {fmtDate(c.preferred_datetime)}
                </td>
                <td className="py-3 px-4 text-forest/70 text-xs">{c.topic}</td>
                <td className="py-3 px-4 text-forest/50 text-xs">{c.source}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </TabPanel>
  );
}

/* ------------------------------------------------------------------
 * Strategy Calls tab
 * ------------------------------------------------------------------ */
function StrategyTab({ calls }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(
    () => calls.filter((c) => matches(c, q, ["name", "email", "mobile", "stage"])),
    [calls, q]
  );
  return (
    <TabPanel
      title="Strategy call bookings"
      subtitle="Escape with Wealth · Legacy CTA"
      count={calls.length}
      exportKind="strategy-calls"
      exportTestId={TID.cpExportStrategy}
    >
      <SearchBar value={q} onChange={setQ} />
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-forest/50 text-xs border-b border-forest/15">
              <th className="text-left py-3 px-4 font-normal">Created</th>
              <th className="text-left py-3 px-4 font-normal">Name</th>
              <th className="text-left py-3 px-4 font-normal">Mobile</th>
              <th className="text-left py-3 px-4 font-normal">Slot</th>
              <th className="text-left py-3 px-4 font-normal">Stage</th>
              <th className="text-left py-3 px-4 font-normal">Source</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-forest/10">
            {filtered.length === 0 && <EmptyRow cols={6} message="No strategy calls yet." />}
            {filtered.map((c) => (
              <tr key={c.id} className="text-forest">
                <td className="py-3 px-4 text-forest/60 text-xs">{fmtDate(c.created_at)}</td>
                <td className="py-3 px-4 font-medium">{c.name}</td>
                <td className="py-3 px-4 text-forest/70">{c.mobile}</td>
                <td className="py-3 px-4 text-forest/70 text-xs">{c.time_slot}</td>
                <td className="py-3 px-4 text-forest/70 text-xs">{c.stage}</td>
                <td className="py-3 px-4 text-forest/50 text-xs">{c.source}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </TabPanel>
  );
}

/* ------------------------------------------------------------------
 * Settings tab — integrations (WhatsApp + CRM)
 * ------------------------------------------------------------------ */
function SettingsTab({ token }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [values, setValues] = useState({
    whatsapp: {
      provider: "meta_cloud_api", phone_number_id: "", access_token: "",
      business_account_id: "", default_template: "", enabled: false,
    },
    crm: {
      provider: "webhook", webhook_url: "", api_key: "",
      header_name: "Authorization", header_prefix: "Bearer", enabled: false,
    },
  });
  const [masks, setMasks] = useState({ whatsapp: "", crm: "" });

  useEffect(() => {
    cpGetIntegrations(token)
      .then((data) => {
        setValues((v) => ({
          whatsapp: {
            provider: data.whatsapp.provider || "meta_cloud_api",
            phone_number_id: data.whatsapp.phone_number_id || "",
            access_token: "",
            business_account_id: data.whatsapp.business_account_id || "",
            default_template: data.whatsapp.default_template || "",
            enabled: !!data.whatsapp.enabled,
          },
          crm: {
            provider: data.crm.provider || "webhook",
            webhook_url: data.crm.webhook_url || "",
            api_key: "",
            header_name: data.crm.header_name || "Authorization",
            header_prefix: data.crm.header_prefix || "Bearer",
            enabled: !!data.crm.enabled,
          },
        }));
        setMasks({
          whatsapp: data.whatsapp.access_token_masked || "",
          crm: data.crm.api_key_masked || "",
        });
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [token]);

  const patch = (section, field, val) =>
    setValues((v) => ({ ...v, [section]: { ...v[section], [field]: val } }));

  const save = async () => {
    setSaving(true);
    try {
      const data = await cpUpdateIntegrations(token, values);
      setMasks({
        whatsapp: data.whatsapp.access_token_masked || "",
        crm: data.crm.api_key_masked || "",
      });
      // Clear the input fields after save so masked values remain the source of truth
      setValues((v) => ({
        whatsapp: { ...v.whatsapp, access_token: "" },
        crm: { ...v.crm, api_key: "" },
      }));
      toast.success("Integrations saved.");
    } catch {
      toast.error("Couldn't save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const testCrm = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await cpTestCRMWebhook(token);
      setTestResult(res);
      if (res.ok) toast.success(`Webhook OK · HTTP ${res.status}`);
      else toast.error(`Webhook failed · HTTP ${res.status}`);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Test failed";
      setTestResult({ ok: false, status: 0, message: detail });
      toast.error(detail);
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white border border-forest/10 rounded-lg p-10 text-center text-forest/50 text-sm">
        Loading settings&hellip;
      </div>
    );
  }

  return (
    <div className="grid md:grid-cols-2 gap-6">
      {/* WhatsApp Business */}
      <div className="bg-white border border-forest/10 rounded-lg p-5 md:p-6 space-y-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="eyebrow text-teal inline-flex items-center gap-2">
              <MessageSquareText size={13} /> WhatsApp Business
            </div>
            <div className="font-display text-forest text-xl mt-1">Outbound messaging</div>
            <p className="text-xs text-forest/60 mt-1 leading-relaxed">
              Store your Meta Cloud API credentials to enable template messages from the portal. Actual send-on-create hooks will roll out next.
            </p>
          </div>
          <ToggleSwitch
            checked={values.whatsapp.enabled}
            onChange={(v) => patch("whatsapp", "enabled", v)}
            testId={TID.cpSettingsWaEnabled}
          />
        </div>

        <FieldGroup label="Provider">
          <select
            data-testid={TID.cpSettingsWaProvider}
            value={values.whatsapp.provider}
            onChange={(e) => patch("whatsapp", "provider", e.target.value)}
            className={inputCls}
          >
            <option value="meta_cloud_api">Meta Cloud API</option>
            <option value="twilio">Twilio</option>
            <option value="custom">Custom / Self-hosted</option>
          </select>
        </FieldGroup>

        <FieldGroup label="Phone Number ID">
          <input
            data-testid={TID.cpSettingsWaPhoneId}
            value={values.whatsapp.phone_number_id}
            onChange={(e) => patch("whatsapp", "phone_number_id", e.target.value)}
            className={inputCls}
            placeholder="e.g. 106540935408123"
          />
        </FieldGroup>

        <FieldGroup label="Business Account ID">
          <input
            value={values.whatsapp.business_account_id}
            onChange={(e) => patch("whatsapp", "business_account_id", e.target.value)}
            className={inputCls}
            placeholder="e.g. 108250045421899"
          />
        </FieldGroup>

        <FieldGroup label={`Access Token ${masks.whatsapp ? `· stored: ${masks.whatsapp}` : ""}`}>
          <input
            data-testid={TID.cpSettingsWaToken}
            value={values.whatsapp.access_token}
            onChange={(e) => patch("whatsapp", "access_token", e.target.value)}
            className={inputCls}
            type="password"
            placeholder={masks.whatsapp ? "Leave blank to keep stored token" : "EAAG…"}
          />
        </FieldGroup>

        <FieldGroup label="Default Template Name">
          <input
            value={values.whatsapp.default_template}
            onChange={(e) => patch("whatsapp", "default_template", e.target.value)}
            className={inputCls}
            placeholder="e.g. chapter_iii_boarding_pass"
          />
        </FieldGroup>
      </div>

      {/* CRM */}
      <div className="bg-white border border-forest/10 rounded-lg p-5 md:p-6 space-y-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="eyebrow text-teal inline-flex items-center gap-2">
              <RefreshCw size={13} /> CRM Sync
            </div>
            <div className="font-display text-forest text-xl mt-1">Push leads to your CRM</div>
            <p className="text-xs text-forest/60 mt-1 leading-relaxed">
              When enabled, every new lead across the site is pushed to your webhook (or NeoDove) with the full payload.
            </p>
          </div>
          <ToggleSwitch
            checked={values.crm.enabled}
            onChange={(v) => patch("crm", "enabled", v)}
            testId={TID.cpSettingsCrmEnabled}
          />
        </div>

        <FieldGroup label="Provider">
          <select
            value={values.crm.provider}
            onChange={(e) => patch("crm", "provider", e.target.value)}
            className={inputCls}
          >
            <option value="webhook">Generic Webhook</option>
            <option value="neodove">NeoDove</option>
            <option value="custom">Custom</option>
          </select>
        </FieldGroup>

        <FieldGroup label="Webhook URL">
          <input
            data-testid={TID.cpSettingsCrmUrl}
            value={values.crm.webhook_url}
            onChange={(e) => patch("crm", "webhook_url", e.target.value)}
            className={inputCls}
            placeholder="https://api.your-crm.com/leads"
          />
        </FieldGroup>

        <FieldGroup label={`API Key ${masks.crm ? `· stored: ${masks.crm}` : ""}`}>
          <input
            data-testid={TID.cpSettingsCrmKey}
            value={values.crm.api_key}
            onChange={(e) => patch("crm", "api_key", e.target.value)}
            className={inputCls}
            type="password"
            placeholder={masks.crm ? "Leave blank to keep stored key" : "sk_live_…"}
          />
        </FieldGroup>

        <div className="grid grid-cols-2 gap-4">
          <FieldGroup label="Auth Header Name">
            <input
              value={values.crm.header_name}
              onChange={(e) => patch("crm", "header_name", e.target.value)}
              className={inputCls}
              placeholder="Authorization"
            />
          </FieldGroup>
          <FieldGroup label="Header Prefix">
            <input
              value={values.crm.header_prefix}
              onChange={(e) => patch("crm", "header_prefix", e.target.value)}
              className={inputCls}
              placeholder="Bearer"
            />
          </FieldGroup>
        </div>

        <div className="flex items-center gap-3 pt-2">
          <button
            data-testid={TID.cpSettingsTestCrm}
            onClick={testCrm}
            disabled={testing || !values.crm.webhook_url}
            className="btn-outline-forest text-sm"
          >
            <Send size={14} />
            {testing ? "Testing…" : "Test webhook"}
          </button>
          {testResult && (
            <div
              className={`text-xs inline-flex items-center gap-2 ${
                testResult.ok ? "text-teal" : "text-forest/70"
              }`}
            >
              {testResult.ok ? <CheckCircle2 size={13} /> : <XCircle size={13} />}
              HTTP {testResult.status || "—"} {testResult.message && `· ${testResult.message.slice(0, 60)}`}
            </div>
          )}
        </div>
      </div>

      {/* Save bar */}
      <div className="md:col-span-2 flex items-center justify-end gap-3">
        <button
          data-testid={TID.cpSettingsSave}
          onClick={save}
          disabled={saving}
          className="btn-primary text-sm shadow-lg"
        >
          {saving ? "Saving…" : "Save integrations"}
        </button>
      </div>

      {/* Danger zone — destructive DB wipe */}
      <div className="md:col-span-2">
        <DangerZone token={token} />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------
 * DangerZone — one-click reset of all lead collections
 * ------------------------------------------------------------------ */
function DangerZone({ token }) {
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [wiping, setWiping] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const CONFIRM_STRING = "WIPE ALL LEADS";

  const run = async () => {
    if (confirm.trim() !== CONFIRM_STRING) {
      toast.error("Please type the confirmation exactly.");
      return;
    }
    setWiping(true);
    try {
      const res = await cpWipeAllLeads(token, confirm.trim());
      setLastResult(res);
      const total = Object.values(res.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(`Wiped ${total} record${total === 1 ? "" : "s"}. Refresh to see counts.`);
      setConfirm("");
      setOpen(false);
      // Refresh the page so all tabs re-fetch fresh (empty) data
      setTimeout(() => window.location.reload(), 900);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Wipe failed";
      toast.error(detail);
    } finally {
      setWiping(false);
    }
  };

  return (
    <div className="mt-8 border-2 border-dashed border-red-300 bg-red-50/40 rounded-lg p-5 md:p-6">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-red-100 grid place-items-center text-red-600 shrink-0">
          <AlertTriangle size={18} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="eyebrow text-red-600">Danger zone</div>
          <div className="font-display text-forest text-xl mt-1">
            Reset all lead data
          </div>
          <p className="text-xs md:text-sm text-forest/70 mt-2 leading-relaxed max-w-xl">
            Deletes every record from Contact Leads, Event Registrations
            (completed &amp; abandoned), Chatbot Leads, Consultations and
            Strategy Calls. Integration settings and blog posts are preserved.
            This action cannot be undone.
          </p>

          {!open ? (
            <button
              data-testid={TID.cpSettingsWipe}
              onClick={() => setOpen(true)}
              className="mt-4 inline-flex items-center gap-2 rounded-full border border-red-500 text-red-600 hover:bg-red-500 hover:text-white transition-colors text-sm font-medium px-4 py-2"
            >
              <Trash2 size={14} />
              Wipe all lead data
            </button>
          ) : (
            <div className="mt-4 space-y-3">
              <label className="block">
                <span className="text-xs text-forest/70 block mb-1">
                  Type <code className="font-mono text-red-600">{CONFIRM_STRING}</code> to enable the button.
                </span>
                <input
                  data-testid={TID.cpSettingsWipeInput}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  autoFocus
                  className="w-full max-w-md border-0 border-b border-red-400 bg-transparent py-2.5 text-red-700 placeholder:text-red-300 focus:outline-none focus:border-red-600 text-sm font-mono"
                  placeholder={CONFIRM_STRING}
                />
              </label>
              <div className="flex items-center gap-3">
                <button
                  data-testid={TID.cpSettingsWipeConfirm}
                  onClick={run}
                  disabled={wiping || confirm.trim() !== CONFIRM_STRING}
                  className="inline-flex items-center gap-2 rounded-full bg-red-600 text-white disabled:bg-red-300 disabled:cursor-not-allowed hover:bg-red-700 transition-colors text-sm font-medium px-4 py-2"
                >
                  <Trash2 size={14} />
                  {wiping ? "Wiping…" : "Yes, delete everything"}
                </button>
                <button
                  data-testid={TID.cpSettingsWipeCancel}
                  onClick={() => {
                    setOpen(false);
                    setConfirm("");
                  }}
                  disabled={wiping}
                  className="text-sm text-forest/70 hover:text-forest"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {lastResult && (
            <div className="mt-4 text-xs text-forest/70 border-l-2 border-forest/20 pl-3">
              Last wipe at {lastResult.at}:
              {Object.entries(lastResult.deleted || {}).map(([k, v]) => (
                <span key={k} className="ml-2">
                  {k}: <strong>{v}</strong>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------
 * Shared helpers / small components
 * ------------------------------------------------------------------ */
const inputCls =
  "w-full border-0 border-b border-forest/25 bg-transparent py-2.5 px-0 text-forest placeholder:text-forest/40 focus:outline-none focus:border-forest text-sm font-body transition-colors";

function FieldGroup({ label, children }) {
  return (
    <label className="block">
      <span className="eyebrow text-forest/60 block mb-1">{label}</span>
      {children}
    </label>
  );
}

function ToggleSwitch({ checked, onChange, testId }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      data-testid={testId}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
        checked ? "bg-forest" : "bg-forest/20"
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-cream transition-transform ${
          checked ? "translate-x-6" : "translate-x-1"
        }`}
      />
    </button>
  );
}

function StatCard({ title, main, sub, highlight }) {
  return (
    <div className={`rounded-lg border p-6 ${highlight ? "bg-forest text-cream border-forest" : "bg-white border-forest/10 text-forest"}`}>
      <div className={`eyebrow ${highlight ? "text-lime" : "text-teal"}`}>{title}</div>
      <div className="font-display text-2xl md:text-3xl leading-tight mt-2">{main}</div>
      <div className={`text-xs mt-3 ${highlight ? "text-cream/70" : "text-forest/60"}`}>{sub}</div>
    </div>
  );
}

function SmallStat({ label, value, icon: Icon, tone }) {
  const toneCls = {
    hot: "bg-lime text-void",
    warm: "bg-teal text-cream",
    cold: "bg-forest text-cream",
  }[tone] || "bg-cream text-forest";
  return (
    <div className="bg-white border border-forest/10 rounded-lg p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-full grid place-items-center ${toneCls}`}>
        <Icon size={16} />
      </div>
      <div>
        <div className="font-display text-forest text-2xl leading-none">{value}</div>
        <div className="eyebrow text-forest/50 mt-1">{label}</div>
      </div>
    </div>
  );
}

function BandChip({ band }) {
  const map = {
    Hot: "bg-lime text-void",
    Warm: "bg-teal text-cream",
    Cold: "bg-forest/20 text-forest",
  };
  return (
    <span className={`inline-block px-2.5 py-1 rounded-full text-[11px] font-semibold ${map[band] || "bg-forest/10 text-forest/40"}`}>
      {band || "—"}
    </span>
  );
}

function StageChip({ label, active, onClick, tone, small }) {
  const toneCls = tone === "ok" ? "border-teal text-teal" : tone === "warn" ? "border-orange-500 text-orange-500" : "border-forest/25 text-forest/70";
  const activeCls = active
    ? "bg-forest text-cream border-forest"
    : `bg-transparent hover:border-forest ${toneCls}`;
  const sizeCls = small ? "text-[11px] px-2.5 py-1" : "text-xs px-3 py-1.5";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center border rounded-full transition-colors ${activeCls} ${sizeCls}`}
    >
      {label}
    </button>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function matches(record, query, fields) {
  if (!query) return true;
  const q = query.toLowerCase().trim();
  return fields.some((f) => String(record?.[f] || "").toLowerCase().includes(q));
}

/* ------------------------------------------------------------------
 * AnalyticsTab — traffic, behavior, marketing, AI insights, reports
 * ------------------------------------------------------------------ */
const SOURCE_COLORS = {
  "Direct": "#0B4A3D",
  "Organic search": "#00A676",
  "Social": "#A6CE39",
  "Paid ads": "#F4B860",
  "Referral": "#8AA6A0",
  "AI search": "#7C5CFA",
};

function AnalyticsTab({ token }) {
  const [days, setDays] = useState(30);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [insights, setInsights] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([cpAnalyticsSummary(token, days), cpAnalyticsInsights(token)])
      .then(([s, i]) => {
        setSummary(s);
        setInsights(i);
      })
      .catch(() => toast.error("Couldn't load analytics."))
      .finally(() => setLoading(false));
  }, [token, days]);

  const regen = async () => {
    setRegenerating(true);
    try {
      const res = await cpRegenerateInsights(token);
      setInsights(res);
      toast.success("Fresh insights generated.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Insight generation failed.");
    } finally {
      setRegenerating(false);
    }
  };

  const downloadReport = async () => {
    setDownloading(true);
    try {
      const resp = await fetch(`${API}/admin/export/analytics.xlsx`, { headers: { "X-CP-Token": token } });
      if (!resp.ok) throw new Error("failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "pali-hill-city-analytics.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Analytics report downloaded.");
    } catch {
      toast.error("Couldn't download the report.");
    } finally {
      setDownloading(false);
    }
  };

  if (loading || !summary) {
    return (
      <div className="bg-white border border-forest/10 rounded-lg p-10 text-center text-forest/50 text-sm">
        Loading analytics&hellip;
      </div>
    );
  }

  const t = summary.totals;

  return (
    <div className="space-y-6">
      {/* Header controls */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="eyebrow text-teal">Analytics · Last {days} days</div>
          <div className="font-display text-forest text-2xl md:text-3xl leading-tight">
            Traffic, behaviour, marketing.
          </div>
        </div>
        <div className="flex items-center gap-3">
          <select
            data-testid={TID.cpAnalyticsRange}
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="text-sm bg-white border border-forest/20 rounded-md px-3 py-2 text-forest focus:outline-none focus:border-forest"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <button
            data-testid={TID.cpAnalyticsExport}
            onClick={downloadReport}
            disabled={downloading}
            className="btn-primary text-sm"
          >
            <Download size={14} />
            {downloading ? "Preparing…" : "Download XLS"}
          </button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <KPI label="Page views" value={t.page_views} icon={Globe2} />
        <KPI label="Unique visitors" value={t.unique_visitors} icon={Users} />
        <KPI label="Sessions" value={t.unique_sessions} icon={TrendingUp} />
        <KPI label="Chat sessions" value={t.chat_sessions} icon={Bot} />
        <KPI label="Total events" value={t.events} icon={MousePointerClick} />
        <KPI label="Avg session" value={fmtDuration(t.avg_session_seconds)} icon={LayoutDashboard} />
        <KPI label="Events / sess" value={t.avg_events_per_session} icon={SparklesIcon} />
      </div>

      {/* Traffic — daily line */}
      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2 bg-white border border-forest/10 rounded-lg p-5 md:p-6 h-[320px]">
          <div className="eyebrow text-teal mb-2">Traffic · Daily (IST)</div>
          <div className="font-display text-forest text-lg">Sessions & page views</div>
          <div className="h-[240px] mt-2">
            {summary.daily.length === 0 ? (
              <EmptyChart />
            ) : (
              <ResponsiveContainer>
                <LineChart data={summary.daily} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke="#0B4A3D" strokeOpacity={0.08} vertical={false} />
                  <XAxis dataKey="day" stroke="#0B4A3D" strokeOpacity={0.5} tick={{ fill: "#0B4A3D", fontSize: 11 }} />
                  <YAxis stroke="#0B4A3D" strokeOpacity={0.5} tick={{ fill: "#0B4A3D", fontSize: 11 }} />
                  <Tooltip contentStyle={{ background: "#0B4A3D", color: "#F4F4EF", border: "none", fontSize: 12 }} />
                  <Line dataKey="sessions" stroke="#0B4A3D" strokeWidth={2.5} dot={{ fill: "#A6CE39", r: 3 }} name="Sessions" />
                  <Line dataKey="views" stroke="#00A676" strokeWidth={2} dot={{ fill: "#00A676", r: 2 }} name="Page views" />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Sources pie */}
        <div className="bg-white border border-forest/10 rounded-lg p-5 md:p-6">
          <div className="eyebrow text-teal mb-2">Traffic · Sources</div>
          <div className="font-display text-forest text-lg mb-2">Where visitors come from</div>
          {summary.top_sources.length === 0 ? (
            <EmptyChart />
          ) : (
            <div className="h-[210px]">
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={summary.top_sources} dataKey="count" nameKey="label" innerRadius={40} outerRadius={80}>
                    {summary.top_sources.map((s, i) => (
                      <Cell key={s.label} fill={SOURCE_COLORS[s.label] || "#B7B4A6"} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ background: "#0B4A3D", color: "#F4F4EF", border: "none", fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
          <div className="mt-2 space-y-1 text-xs">
            {summary.top_sources.map((s) => (
              <div key={s.label} className="flex items-center justify-between">
                <span className="inline-flex items-center gap-2 text-forest/70">
                  <span className="w-2 h-2 rounded-full" style={{ background: SOURCE_COLORS[s.label] || "#B7B4A6" }} />
                  {s.label}
                </span>
                <span className="text-forest font-mono">{s.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Behavior + Marketing row */}
      <div className="grid md:grid-cols-2 gap-6">
        <RankBar title="Top pages" subtitle="Most-viewed URLs" data={summary.top_pages} valueLabel="views" />
        <RankBar title="Top CTAs" subtitle="Buttons your visitors click" data={summary.top_ctas} valueLabel="clicks" />
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <RankBar title="Top countries" subtitle="Geo distribution" data={summary.top_countries} valueLabel="views" />
        <RankBar title="AI search appearances" subtitle="Answer-engine crawler hits" data={summary.ai_bot_hits} valueLabel="hits" emptyMessage="No AI-bot hits yet. Once you submit sitemap to Google Search Console, GPTBot / PerplexityBot / ClaudeBot will start crawling and register here." />
      </div>

      {/* Form funnels */}
      <div className="bg-white border border-forest/10 rounded-lg p-5 md:p-6">
        <div className="eyebrow text-teal mb-2">Marketing · Form funnels</div>
        <div className="font-display text-forest text-lg mb-4">Open → advance → submit conversion</div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {Object.entries(summary.funnels).map(([form, f]) => (
            <FunnelCard key={form} form={form} data={f} />
          ))}
        </div>
      </div>

      {/* Search + AI Insights */}
      <div className="grid md:grid-cols-5 gap-6">
        <div className="md:col-span-2 bg-white border border-forest/10 rounded-lg p-5 md:p-6">
          <div className="eyebrow text-teal mb-2">Marketing · On-site search</div>
          <div className="font-display text-forest text-lg mb-4">Top searched queries</div>
          {summary.search_queries.length === 0 ? (
            <div className="text-forest/40 text-sm py-4">No searches recorded yet.</div>
          ) : (
            <ul className="space-y-2">
              {summary.search_queries.map((q) => (
                <li key={q.label} className="flex items-center justify-between text-sm border-b border-forest/5 pb-2">
                  <span className="text-forest/80 truncate">&quot;{q.label}&quot;</span>
                  <span className="font-mono text-forest text-xs">{q.count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="md:col-span-3 bg-forest text-cream rounded-lg p-5 md:p-6 relative overflow-hidden">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="eyebrow text-lime inline-flex items-center gap-2">
                <SparklesIcon size={13} /> AI Insights &middot; Claude Sonnet
              </div>
              <div className="font-display text-2xl mt-1">Growth recommendations</div>
              <div className="text-xs text-cream/50 mt-1">
                {insights?.generated_at
                  ? `Last generated ${new Date(insights.generated_at).toLocaleString("en-IN")}`
                  : "Not generated yet — click Regenerate to run."}
              </div>
            </div>
            <button
              data-testid={TID.cpAnalyticsRegenerate}
              onClick={regen}
              disabled={regenerating}
              className="inline-flex items-center gap-2 rounded-full bg-lime text-void hover:bg-lime/90 disabled:opacity-60 transition-colors text-sm font-semibold px-4 py-2"
            >
              <RefreshCw size={14} className={regenerating ? "animate-spin" : ""} />
              {regenerating ? "Analysing…" : "Regenerate"}
            </button>
          </div>

          {insights?.insights ? (
            <div className="text-sm text-cream/85 leading-relaxed whitespace-pre-line">
              {insights.insights}
            </div>
          ) : (
            <div className="text-sm text-cream/60">
              Once you have at least a few days of traffic, click <em>Regenerate</em> to get a
              plain-English analysis of what&apos;s working, what isn&apos;t, and 3 concrete
              next-steps.
            </div>
          )}

          {insights?.recommendations?.length > 0 && (
            <div className="mt-5">
              <div className="eyebrow text-lime mb-2">Next 3 actions</div>
              <ol className="space-y-2">
                {insights.recommendations.map((r, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm">
                    <span className="inline-flex w-6 h-6 rounded-full bg-lime text-void grid place-items-center text-xs font-semibold shrink-0">
                      {i + 1}
                    </span>
                    <span className="text-cream/85 leading-relaxed">{r}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function KPI({ label, value, icon: Icon }) {
  return (
    <div className="bg-white border border-forest/10 rounded-lg p-3 flex items-center gap-3">
      <div className="w-9 h-9 rounded-full grid place-items-center bg-cream text-forest">
        <Icon size={14} />
      </div>
      <div className="min-w-0">
        <div className="font-display text-forest text-xl leading-none truncate">{value}</div>
        <div className="eyebrow text-forest/50 mt-1 text-[10px]">{label}</div>
      </div>
    </div>
  );
}

function RankBar({ title, subtitle, data, valueLabel, emptyMessage }) {
  const top = (data || []).slice(0, 8);
  const max = Math.max(1, ...top.map((r) => r.count));
  return (
    <div className="bg-white border border-forest/10 rounded-lg p-5 md:p-6">
      <div className="eyebrow text-teal mb-2">{title}</div>
      <div className="font-display text-forest text-lg mb-4">{subtitle}</div>
      {top.length === 0 ? (
        <div className="text-forest/40 text-sm py-4">{emptyMessage || "No data yet."}</div>
      ) : (
        <div className="space-y-2">
          {top.map((row) => (
            <div key={row.label} className="text-sm">
              <div className="flex items-center justify-between mb-1">
                <span className="text-forest/80 truncate max-w-[70%]">{row.label}</span>
                <span className="text-forest text-xs font-mono">
                  {row.count} <span className="text-forest/40">{valueLabel}</span>
                </span>
              </div>
              <div className="h-2 bg-forest/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-forest rounded-full transition-all"
                  style={{ width: `${(row.count / max) * 100}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FunnelCard({ form, data }) {
  const opens = data.opens || 0;
  const submits = data.submits || 0;
  const rate = opens > 0 ? Math.round((submits / opens) * 100) : 0;
  return (
    <div className="rounded-lg border border-forest/10 bg-cream/40 p-4">
      <div className="eyebrow text-forest/60 capitalize">{form.replaceAll("_", " ")}</div>
      <div className="font-display text-forest text-2xl mt-1 leading-none">
        {rate}
        <span className="text-sm text-forest/50 font-body">%</span>
      </div>
      <div className="text-xs text-forest/50 mt-1">Open → submit conversion</div>
      <div className="mt-3 space-y-1 text-xs">
        <FunnelRow label="Opens" v={opens} max={opens || 1} />
        <FunnelRow label="Advance" v={data.step_advance || 0} max={opens || 1} />
        <FunnelRow label="Submit" v={submits} max={opens || 1} tone="ok" />
        {data.errors > 0 && <FunnelRow label="Errors" v={data.errors} max={opens || 1} tone="err" />}
      </div>
    </div>
  );
}

function FunnelRow({ label, v, max, tone }) {
  const pct = Math.min(100, (v / max) * 100);
  const bar = tone === "ok" ? "bg-teal" : tone === "err" ? "bg-red-500" : "bg-forest/50";
  return (
    <div>
      <div className="flex items-center justify-between text-forest/70">
        <span>{label}</span>
        <span className="font-mono">{v}</span>
      </div>
      <div className="h-1.5 bg-forest/10 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${bar}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function EmptyChart() {
  return (
    <div className="h-full grid place-items-center text-forest/40 text-sm">
      Not enough data yet. Analytics will populate as visitors browse the site.
    </div>
  );
}

function fmtDuration(sec) {
  if (!sec) return "0s";
  if (sec < 60) return `${Math.round(sec)}s`;
  return `${Math.floor(sec / 60)}m ${Math.round(sec % 60)}s`;
}


/* ------------------------------------------------------------------
 * ConsultantsTab — admin directory: list, add, edit, delete, CSV upload
 * ------------------------------------------------------------------ */
function ConsultantsTab({ token }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // {} for new, {…} for edit
  const [csvOpen, setCsvOpen] = useState(false);

  const refresh = () => {
    setLoading(true);
    cpListConsultants(token)
      .then(setItems)
      .catch(() => toast.error("Couldn't load consultants."))
      .finally(() => setLoading(false));
  };

  useEffect(refresh, [token]); // eslint-disable-line

  const filtered = useMemo(
    () => items.filter((c) => matches(c, q, ["name", "firm_name", "representative_name", "mobile", "email", "rera_number"])),
    [items, q]
  );

  const save = async (data) => {
    try {
      if (editing?.id) {
        await cpUpdateConsultant(token, editing.id, data);
      } else {
        await cpCreateConsultant(token, data);
      }
      toast.success(editing?.id ? "Consultant updated." : "Consultant added.");
      setEditing(null);
      refresh();
    } catch {
      toast.error("Save failed.");
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.name}"? This can't be undone.`)) return;
    try {
      await cpDeleteConsultant(token, c.id);
      toast.success("Deleted.");
      refresh();
    } catch {
      toast.error("Delete failed.");
    }
  };

  return (
    <>
      <TabPanel
        title="Consultants directory"
        subtitle="Admin-managed referral partners · Visible in the registration form typeahead"
        count={items.length}
      >
        <div className="p-4 md:p-5 border-b border-forest/10 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 justify-between">
          <div className="relative flex-1 max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-forest/40" />
            <input
              data-testid={TID.cpConsultantSearch}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, firm, mobile, RERA, email…"
              className="w-full pl-9 pr-3 py-2 text-sm bg-cream/60 border border-forest/15 rounded-md focus:outline-none focus:border-forest text-forest placeholder:text-forest/40"
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid={TID.cpConsultantUpload}
              onClick={() => setCsvOpen(true)}
              className="btn-outline-forest text-sm"
            >
              <Upload size={14} /> Upload CSV
            </button>
            <button
              data-testid={TID.cpConsultantAdd}
              onClick={() => setEditing({})}
              className="btn-primary text-sm"
            >
              <Plus size={14} /> Add consultant
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-forest/50 text-xs border-b border-forest/15">
                <th className="text-left py-3 px-4 font-normal">Representative</th>
                <th className="text-left py-3 px-4 font-normal">Firm</th>
                <th className="text-left py-3 px-4 font-normal">Mobile</th>
                <th className="text-left py-3 px-4 font-normal">Email</th>
                <th className="text-left py-3 px-4 font-normal">RERA</th>
                <th className="text-right py-3 px-4 font-normal w-28">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-forest/10">
              {loading && <EmptyRow cols={6} message="Loading…" />}
              {!loading && filtered.length === 0 && (
                <EmptyRow cols={6} message='No consultants yet. Click "Add consultant" or "Upload CSV".' />
              )}
              {!loading && filtered.map((c) => (
                <tr key={c.id} className="text-forest">
                  <td className="py-3 px-4 font-medium">{c.representative_name || c.name || "—"}</td>
                  <td className="py-3 px-4 text-forest/70 text-xs">{c.firm_name || "—"}</td>
                  <td className="py-3 px-4 text-forest/70 text-xs">{c.mobile || "—"}</td>
                  <td className="py-3 px-4 text-forest/70 text-xs">{c.email || "—"}</td>
                  <td className="py-3 px-4 text-forest/70 text-xs font-mono">{c.rera_number || "—"}</td>
                  <td className="py-3 px-4 text-right">
                    <button onClick={() => setEditing(c)} className="text-teal hover:text-forest text-xs mr-3 inline-flex items-center gap-1">
                      <Pencil size={12} /> Edit
                    </button>
                    <button
                      data-testid={`${TID.cpConsultantDelete}-${c.id}`}
                      onClick={() => remove(c)}
                      className="text-red-500 hover:text-red-700 text-xs inline-flex items-center gap-1"
                    >
                      <Trash2 size={12} /> Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </TabPanel>

      {editing && <ConsultantEditor value={editing} onSave={save} onCancel={() => setEditing(null)} />}
      {csvOpen && (
        <ConsultantCsvUploader
          token={token}
          onClose={(reload) => {
            setCsvOpen(false);
            if (reload) refresh();
          }}
        />
      )}
    </>
  );
}

function ConsultantEditor({ value, onSave, onCancel }) {
  const [v, setV] = useState({
    name: value?.name || "",
    firm_name: value?.firm_name || "",
    representative_name: value?.representative_name || "",
    mobile: value?.mobile || "",
    email: value?.email || "",
    rera_number: value?.rera_number || "",
  });
  const set = (k) => (e) => setV({ ...v, [k]: e.target.value });
  const submit = (e) => {
    e.preventDefault();
    // Representative name is what users remember and search by. Firm/mobile/
    // email are required so the record is actionable for sales follow-up.
    if (!v.representative_name.trim()) {
      toast.error("Representative name is required.");
      return;
    }
    if (!v.firm_name.trim() || !v.mobile.trim() || !v.email.trim()) {
      toast.error("Firm, mobile and email are required.");
      return;
    }
    // Mirror representative_name into the legacy `name` field so search + old
    // UI paths still work.
    onSave({ ...v, name: v.name.trim() || v.representative_name.trim() });
  };
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-void/50 backdrop-blur-sm p-4">
      <form onSubmit={submit} className="bg-white rounded-2xl w-full max-w-lg p-6 md:p-8 space-y-4">
        <div>
          <div className="eyebrow text-teal">Consultant</div>
          <div className="font-display text-forest text-2xl">
            {value?.id ? "Edit consultant" : "Add consultant"}
          </div>
        </div>
        <FieldRow>
          <FieldGroup label="Representative name *">
            <input value={v.representative_name} onChange={set("representative_name")} className={inputCls} required />
          </FieldGroup>
          <FieldGroup label="Firm name *">
            <input value={v.firm_name} onChange={set("firm_name")} className={inputCls} required />
          </FieldGroup>
        </FieldRow>
        <FieldRow>
          <FieldGroup label="Mobile *">
            <input value={v.mobile} onChange={set("mobile")} className={inputCls} required />
          </FieldGroup>
          <FieldGroup label="Email *">
            <input type="email" value={v.email} onChange={set("email")} className={inputCls} required />
          </FieldGroup>
        </FieldRow>
        <FieldRow>
          <FieldGroup label="RERA number">
            <input value={v.rera_number} onChange={set("rera_number")} className={inputCls} />
          </FieldGroup>
          <FieldGroup label="Display name (optional)">
            <input
              value={v.name}
              onChange={set("name")}
              className={inputCls}
              placeholder="Auto — same as representative"
            />
          </FieldGroup>
        </FieldRow>
        <div className="flex items-center justify-end gap-3 pt-2">
          <button type="button" onClick={onCancel} className="text-sm text-forest/70 hover:text-forest">Cancel</button>
          <button type="submit" data-testid={TID.cpConsultantSave} className="btn-primary text-sm">
            {value?.id ? "Save changes" : "Add"}
          </button>
        </div>
      </form>
    </div>
  );
}

function ConsultantCsvUploader({ token, onClose }) {
  const [text, setText] = useState("");
  const [replace, setReplace] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setText(await f.text());
  };

  const run = async () => {
    if (!text.trim()) return;
    setRunning(true);
    try {
      const res = await cpUploadConsultantsCsv(token, text, replace);
      setResult(res);
      toast.success(`Inserted ${res.inserted} rows.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-void/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl w-full max-w-xl p-6 md:p-8 space-y-4">
        <div>
          <div className="eyebrow text-teal">Consultants</div>
          <div className="font-display text-forest text-2xl">Bulk upload · CSV</div>
          <p className="text-xs text-forest/60 mt-2 leading-relaxed">
            CSV must have a header row. Required columns per row:{" "}
            <code className="font-mono">representative_name</code>,{" "}
            <code className="font-mono">firm_name</code>,{" "}
            <code className="font-mono">mobile</code>,{" "}
            <code className="font-mono">email</code>. Optional:{" "}
            <code className="font-mono">rera_number</code>, <code className="font-mono">name</code>{" "}
            (legacy alias — falls back to <code className="font-mono">representative_name</code>).
          </p>
        </div>
        <input type="file" accept=".csv,text/csv" onChange={onFile} className="text-sm" />
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={8}
          placeholder="…or paste CSV text here"
          className="w-full text-sm font-mono border border-forest/15 rounded-md p-3 focus:outline-none focus:border-forest"
        />
        <label className="flex items-center gap-2 text-xs text-forest/70">
          <input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} />
          Replace all existing consultants with this file
        </label>
        {result && (
          <div className="text-xs text-forest/70 bg-cream/60 p-3 rounded-md">
            Inserted {result.inserted}. {result.errors.length > 0 && <>Errors: {result.errors.join("; ")}</>}
          </div>
        )}
        <div className="flex items-center justify-end gap-3">
          <button onClick={() => onClose(!!result)} className="text-sm text-forest/70 hover:text-forest">Close</button>
          <button onClick={run} disabled={running || !text.trim()} className="btn-primary text-sm">
            {running ? "Uploading…" : "Import"}
          </button>
        </div>
      </div>
    </div>
  );
}

function FieldRow({ children }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>;
}

